import { MasterTradeParams } from 'app/models/master-trade-params'
import { Menu, Tooltip, Overlay, Stack, Text } from '@mantine/core'
import { CustomLoader } from 'app/views/components/loader/loader'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import IonIcon from '@reacticons/ionicons'
import { useAuth0 } from '@auth0/auth0-react'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation'
import PrimaryDropDown from 'app/views/components/DropDown/DropDown'
import { useMatch, useNavigate } from 'react-router-dom'
import { deleteDraftMasterTrade } from 'app/state/ducks/master-trades/thunks'
import { getSignableDocuments } from 'app/state/ducks/signable-document/selectors'
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors'
import { getDocuments } from 'app/state/ducks/documents/selectors'
import { SubTradeParams } from 'app/models/sub-trade-params'
import { EntityParams } from 'app/models/entity-params'
import { ThirdPartyRoles } from 'app/models/entity-profile-params'
import { DealParams } from 'app/models/deal-params'
import { Document, SignableDocument } from 'app/models/document-params'
import { Hold } from 'app/models/hold-params'
import { ContractType, DocumentStateTypes } from 'app/models/common-types'
import PlaceHoldModal from 'app/views/components/holds/place-hold-modal'
import LiftHoldModal from 'app/views/components/holds/lift-hold-modal'
import { HoldPayload } from 'app/views/components/holds/hold-form-utils'
import { useSelectedSubTrades } from './selected-sub-trades-context'
import {
  createSignableDocumentPackets,
  placeHoldOnSubTrades,
  liftHoldOnSubTrades,
  getSubTradeSignableDocument,
} from 'app/state/ducks/signable-document/thunks'
import { downloadFromUrl } from 'app/utils/util-functions'
import { convertHtmlToPdfBlob } from 'app/utils/htmlToPdf'
import {
  DEFAULT_OPERATIONS_EMAIL,
  arrayBufferToBase64,
  buildTradeEml,
  derivePdfContents,
  extractSubtradeIdFromFileName,
  formatEmailDate,
  slugifyShortName,
} from './trade-eml-builder'
import { generateOpenTradePacketEml } from './open-trade-packet-generator'
import {
  prepareDelivery,
  markSent,
  markPrepFailed,
  markSendFailed,
} from 'app/state/ducks/trade-document-delivery/thunks'
import { loadDeliveries } from 'app/state/ducks/trade-document-delivery/actions'
import { getDeliveries } from 'app/state/ducks/trade-document-delivery/selectors'
import { useDeliveryConfirm } from './delivery-confirm-context'
import { mostCurrentDelivery } from './documents/delivery-view'
import { useTradePageDeals, useTradePageEntities } from 'app/views/pages/trade-entry/detail-trade/use-trade-page-lookups'
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh'

type Props = {
  row?: MasterTradeParams
}

// Gates the "Place Lock" action: a trade already carrying an active,
// directly-placed (not cascaded) LendOS Trade-level hold can't be locked
// again until that hold is lifted.
export const alreadyLocked = (holds: Hold[]) =>
  holds.some(h => !h.liftedAt && h.system === 'LendOS' && h.origin.level === 'Trade')

export default function TradeActions({ row }: Props) {
  const dispatch = useDispatch()
  const { refreshDocuments } = useTradeRefresh()
  const navigate = useNavigate()
  const { user } = useAuth0()
  const subTrades: SubTradeParams[] = useSelector(getSubTrades)
  const allEntities: EntityParams[] = useTradePageEntities()
  const allDeals: DealParams[] = useTradePageDeals()
  const allAttachments = useSelector(getDocuments)
  const masterTradeAttachments: Document[] =
    allAttachments[DocumentStateTypes[ContractType.MasterTrade]] ?? []
  const signableDocuments: SignableDocument[] = useSelector(getSignableDocuments)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showPlaceLockModal, setShowPlaceLockModal] = useState<boolean>(false)
  const [showLiftLockModal, setShowLiftLockModal] = useState<boolean>(false)
  const [downloadingPacket, setDownloadingPacket] = useState<boolean>(false)
  const [downloadingEml, setDownloadingEml] = useState<boolean>(false)
  const [preparingOpenPacket, setPreparingOpenPacket] = useState<boolean>(false)
  const [markingSent, setMarkingSent] = useState(false)
  const { selectedSubTradeIds } = useSelectedSubTrades()
  const hasSelection = selectedSubTradeIds.length > 0
  // Mark Sent is enabled only when EVERY selected row is Prepared (per product):
  // a gray (no record) or already-Sent row anywhere in the selection disables it.
  // Packet-aware (ADR_LOS-026): the action targets whichever packet the
  // selection's most-current Prepared records belong to — Open and Settled
  // behave identically — and disables when the selection mixes packet types
  // so one bulk confirm can never span two different packets.
  const deliveries = useSelector(getDeliveries)
  const preparedPacketFor = (id: string) => {
    const prepared = deliveries.filter(d => d.subTradeId === id && d.status === 'Prepared')
    const current = mostCurrentDelivery(prepared)
    return current ? (current.packetType ?? 'settled') : null
  }
  const selectedPreparedPackets = selectedSubTradeIds.map(preparedPacketFor)
  const markSentPacketType =
    hasSelection &&
      selectedPreparedPackets[0] !== null &&
      selectedPreparedPackets.every(p => p === selectedPreparedPackets[0])
      ? selectedPreparedPackets[0]
      : null
  const selectedAllPrepared = markSentPacketType !== null
  const isDocumentsTab = !!useMatch('/tradeentry/:tradeId/documents')
  const { openConfirm } = useDeliveryConfirm()

  // For each selected sub-trade whose Funding Memo is UNLOCKED, render the live
  // FM (previewHtml) to a PDF client-side (pdfMake) so the packet embeds the
  // current values rather than the potentially-stale/absent DocuSeal FM. Locked
  // FMs return no previewHtml, so they are left to the backend (DocuSeal).
  const buildUnlockedFmOverrides = async (
    subTradeIds: string[],
  ): Promise<{ subTradeId: string; fmPdfBase64: string }[]> => {
    const overrides = await Promise.all(
      subTradeIds.map(async subTradeId => {
        const res: any = await getSubTradeSignableDocument(subTradeId, 'FundingMemoDocument')
        const previewHtml: string | null = res?.success ? res.payload?.previewHtml ?? null : null
        if (!previewHtml) return null
        try {
          const blobUrl = await convertHtmlToPdfBlob(previewHtml)
          const bytes = await (await fetch(blobUrl)).arrayBuffer()
          URL.revokeObjectURL(blobUrl)
          return { subTradeId, fmPdfBase64: arrayBufferToBase64(bytes) }
        } catch {
          // Fall back to the backend's FM for this sub-trade on conversion failure.
          return null
        }
      }),
    )
    return overrides.filter(
      (o): o is { subTradeId: string; fmPdfBase64: string } => o != null,
    )
  }

  const handleDownloadEml = async () => {
    if (!hasSelection || downloadingEml) return
    setDownloadingEml(true)
    try {
      const fmOverrides = await buildUnlockedFmOverrides(selectedSubTradeIds)
      const response = await createSignableDocumentPackets(selectedSubTradeIds, fmOverrides)
      if (!response.success) {
        ErrorNotification({ title: 'EML failed', message: response.payload })
        return
      }
      const packets: {
        subTradeId: string
        url: string
        fileName: string
        settlementDate?: string | null
      }[] = response.payload

      const tradeDate = formatEmailDate(row?.tradeDate)
      const paymentMethod = row?.paymentType ?? ''

      // Agent Role feeds the Open Trade Packet EML only — it is excluded from
      // the Settlement Packet's recipient list.
      const collectEmails = (entity: EntityParams | null | undefined): string[] => {
        if (!entity?.deliverySettings?.length) return []
        return entity.deliverySettings
          .filter(s => s.deliveryMethod === 'Email' && s.deliveryRole !== ThirdPartyRoles.AgentRole)
          .flatMap(s => s.deliveryEmails ?? [])
          .map(e => e.trim())
          .filter(Boolean)
      }

      const emls = await Promise.all(
        packets.map(async p => {
          const res = await fetch(p.url)
          if (!res.ok) throw new Error(`Failed to fetch packet ${p.subTradeId}`)
          const pdfBase64 = arrayBufferToBase64(await res.arrayBuffer())
          const pdfFileName = p.fileName || `sub-trade-${p.subTradeId}-packet.pdf`

          const st = subTrades.find(s => s.id === p.subTradeId)
          const subtradeIdLabel =
            st?.systemId ||
            extractSubtradeIdFromFileName(pdfFileName) ||
            st?.clientId ||
            p.subTradeId
          // Source the EML's Settlement Date (subject + body) from the packet
          // response so it always matches the SD printed on the Trade Packet:
          // for settled sub-trades that is the actual effective date, which
          // the sub-trade's targetSettlementDate (the plan) may not equal.
          const settlementDate = formatEmailDate(
            p.settlementDate ?? st?.targetSettlementDate,
          )
          const pdfContents = derivePdfContents(masterTradeAttachments, signableDocuments, p.subTradeId)
          const sellerEntity =
            allEntities.find(e => e.id === st?.sellerId?.id) ??
            st?.sellerEntity ??
            row?.sellerEntity
          const buyerEntity =
            allEntities.find(e => e.id === st?.buyerId?.id) ??
            st?.buyerEntity ??
            row?.buyerEntity
          const matchingDeal = allDeals.find(d => d.id === row?.dealId?.id)
          const sellerName = sellerEntity?.entityName ?? ''
          const buyerName = buyerEntity?.entityName ?? ''
          const dealName = matchingDeal?.dealName ?? st?.dealName ?? row?.dealName ?? ''

          // Combine seller + buyer Delivery Settings emails into one To: list (deduped).
          const combinedTo = Array.from(
            new Set([
              ...collectEmails(sellerEntity),
              ...collectEmails(buyerEntity),
            ]),
          )

          const eml = buildTradeEml({
            subtradeIdLabel,
            tradeDate,
            settlementDate,
            dealName,
            sellerName,
            buyerName,
            paymentMethod,
            pdfContents,
            to: combinedTo,
            operationsEmail: DEFAULT_OPERATIONS_EMAIL,
            pdfFileName,
            pdfBase64,
          })

          const idLabel = subtradeIdLabel || p.subTradeId
          const sellerSlug = slugifyShortName(sellerName)
          const buyerSlug = slugifyShortName(buyerName)
          const fileName = [idLabel, sellerSlug, buyerSlug]
            .filter(Boolean)
            .join('__')
          return { fileName: `${fileName}.eml`, eml }
        }),
      )

      // Stagger downloads: browsers throttle/drop multiple anchor-click
      // downloads fired synchronously, so space them out by 100ms each.
      emls.forEach((e, index) => {
        setTimeout(() => {
          const blob = new Blob([e.eml], { type: 'message/rfc822' })
          const url = URL.createObjectURL(blob)

          downloadFromUrl(url, e.fileName)
          setTimeout(() => URL.revokeObjectURL(url), 30000)

        }, index * 100)
      })

      SuccessNotification({
        title: 'EML ready',
        message: `Prepared ${emls.length} email(s). Review and send in Outlook.`,
      })
      const prep: any = await prepareDelivery(row?.id ?? '', selectedSubTradeIds, 'settled')
      if (prep.success) {
        // Apply the authoritative records returned by the endpoint straight to
        // the store — the blue Prepared icon must not depend on PQS ingestion.
        // No deferred reload: the confirm popup below refreshes on close, so
        // the next read is user-gated and can never fire after navigation.
        dispatch(loadDeliveries(prep.payload ?? []))
        // Ask for confirmation across the whole selection (consistent with the
        // Open Trade Packet flow); one Confirm flips every prepared record.
        // Dismissing keeps them Prepared — blue icons / Mark Sent still work.
        openConfirm(selectedSubTradeIds, 'settled')
      } else {
        ErrorNotification({ title: 'Prepare failed', message: String(prep.payload) })
        // The .eml was generated but the Prepared record was not written — the
        // prepare OPERATION failed. Record it (best-effort) so the icon offers
        // the retry affordance instead of silently keeping its old state.
        const failed: any = await markPrepFailed(row?.id ?? '', selectedSubTradeIds, 'settled')
        if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
      }
    } catch (e: any) {
      ErrorNotification({ title: 'EML failed', message: e?.message || 'Failed to build EML' })
      // Record the outcome so the icon renders Prep Failed (LOS-6471).
      const failed: any = await markPrepFailed(row?.id ?? '', selectedSubTradeIds, 'settled')
      if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
    } finally {
      setDownloadingEml(false)
    }
  }

  // Open Trade Packet (ADR_LOS-026): one .eml per master trade covering the
  // selected sub-trades, with two zips grouped by doc type (AA-AL, ADF-Tax
  // Form). Recipients come from the agent entity's Agent Role delivery emails.
  const handlePrepareOpenTradePacket = async () => {
    if (!hasSelection || preparingOpenPacket) return
    setPreparingOpenPacket(true)
    try {
      const result = await generateOpenTradePacketEml(selectedSubTradeIds)
      if (!result.ok) {
        ErrorNotification({
          title: result.blocked ? 'Cannot prepare Open Trade Packet' : 'Open Trade Packet failed',
          message: result.message,
        })
        // A BLOCK is the rule working, not a failure: recording Prep Failed
        // would change the Delivery icon and offer a retry that cannot succeed
        // until a signature lands (LOS-6934 AC7). Only real failures are
        // recorded, so the icon still reports them (LOS-6471).
        if (!result.blocked) {
          const failed: any = await markPrepFailed(row?.id ?? '', selectedSubTradeIds, 'open')
          if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
        }
        return
      }

      SuccessNotification({
        title: 'Open Trade Packet ready',
        message: 'Prepared 1 email. Review and send in Outlook.',
      })

      const prep: any = await prepareDelivery(row?.id ?? '', selectedSubTradeIds, 'open')
      if (prep.success) {
        // Apply the authoritative records returned by the endpoint straight to
        // the store — the blue Prepared icon must not depend on PQS ingestion.
        // No deferred reload: the confirm popup below refreshes on close, so
        // the next read is user-gated and can never fire after navigation.
        dispatch(loadDeliveries(prep.payload ?? []))
        // One email covers the whole selection, so one Confirm flips every
        // selected record (dismiss keeps them Prepared; blue icons re-open).
        openConfirm(selectedSubTradeIds, 'open')
      } else {
        ErrorNotification({ title: 'Prepare failed', message: String(prep.payload) })
        // The .eml was generated but the Prepared record was not written — the
        // prepare OPERATION failed. Record it (best-effort) so the icon offers
        // the retry affordance instead of silently keeping its old state.
        const failed: any = await markPrepFailed(row?.id ?? '', selectedSubTradeIds, 'open')
        if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
      }
    } catch (e: any) {
      ErrorNotification({
        title: 'Open Trade Packet failed',
        message: e?.message || 'Failed to build the Open Trade Packet',
      })
      // Record the outcome so the icon renders Prep Failed (LOS-6471).
      const failed: any = await markPrepFailed(row?.id ?? '', selectedSubTradeIds, 'open')
      if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
    } finally {
      setPreparingOpenPacket(false)
    }
  }

  const handleMarkSent = async () => {
    if (!selectedAllPrepared || markingSent) return
    setMarkingSent(true)
    try {
      const packetType = markSentPacketType ?? 'settled'
      const res: any = await markSent(row?.id ?? '', selectedSubTradeIds, packetType)
      if (res.success) {
        // The endpoint returns the authoritative post-state (status + sentAt),
        // so apply it straight to the store — no lagging PQS re-read needed.
        const records = res.payload ?? []
        SuccessNotification({ title: 'Marked sent', message: `Marked ${records.length} as sent.` })
        dispatch(loadDeliveries(records))
      } else {
        ErrorNotification({ title: 'Mark sent failed', message: String(res.payload) })
        // Record the outcome so the icon renders Send Failed (LOS-6471).
        const failed: any = await markSendFailed(row?.id ?? '', selectedSubTradeIds, packetType)
        if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
      }
    } finally {
      setMarkingSent(false)
    }
  }

  const handleDownloadPacket = async () => {
    if (!hasSelection || downloadingPacket) return
    setDownloadingPacket(true)
    try {
      const fmOverrides = await buildUnlockedFmOverrides(selectedSubTradeIds)
      const response = await createSignableDocumentPackets(selectedSubTradeIds, fmOverrides)
      if (!response.success) {
        ErrorNotification({ title: 'Packet failed', message: response.payload })
        return
      }
      // Stagger downloads: browsers throttle/drop multiple anchor-click
      // downloads fired synchronously, so space them out by 100ms each.
      const packets: {
        subTradeId: string
        url: string
        fileName: string
        settlementDate?: string | null
      }[] = response.payload
      packets.forEach((p, index) => {
        setTimeout(() => {
          downloadFromUrl(p.url, p.fileName || `sub-trade-${p.subTradeId}-packet.pdf`)
        }, index * 100)
      })
      SuccessNotification({ title: 'Packets ready', message: `Downloaded ${response.payload.length} packet(s).` })
    } finally {
      setDownloadingPacket(false)
    }
  }

  const handleDeleteDraftTrade = async (tradeId: string) => {
    const result: any = await dispatch(deleteDraftMasterTrade(tradeId))
    setShowDeleteModal(false)
    if (!result.success) {
      ErrorNotification({ message: result.payload, title: 'Error' })
      return
    }
    SuccessNotification({
      message: 'Draft successfully deleted.',
      title: 'Success!',
    })
    navigate('/tradeentry')
  }

  const tradeId = row?.id
  const isDraft = row?.status === 'Draft'
  const isOpen = row?.status === 'Open'

  const isDocActivelyLocked = (doc: SignableDocument) =>
    (doc.holds ?? []).some(h => !h.liftedAt)

  // Sub-trades in the current selection that are locked (any of their documents
  // has an active, unlifted hold).
  const lockedSelectedSubTradeIds = selectedSubTradeIds.filter(id =>
    (signableDocuments ?? []).some(
      doc => doc.contractId === id && isDocActivelyLocked(doc),
    ),
  )
  // Active holds across the SELECTED sub-trades, deduped by lockId — gates both
  // Place and Lift. Scoped to the current selection (NOT the whole master): an
  // unlocked sub-trade must allow Place and disallow Lift even when a sibling
  // sub-trade of the same master is locked.
  const selectedSubTradeIdSet = new Set(selectedSubTradeIds)
  const tradeHolds: Hold[] = (() => {
    const seen = new Set<string>()
    const result: Hold[] = []
    for (const doc of signableDocuments ?? []) {
      if (!selectedSubTradeIdSet.has(doc.contractId)) continue
      for (const h of doc.holds ?? []) {
        if (!h || h.liftedAt || seen.has(h.lockId)) continue
        seen.add(h.lockId)
        result.push(h)
      }
    }
    return result
  })()
  const isTradeAlreadyLocked = alreadyLocked(tradeHolds)

  // Lift is enabled only when EVERY selected sub-trade carries an active LendOS
  // Trade-level lock (all selected are eligible), mirroring Place — which is
  // enabled only when none are locked. A partial selection (some locked, some
  // not) disables both, so neither action silently skips ineligible trades.
  const allSelectedTradeLocked =
    hasSelection &&
    selectedSubTradeIds.every(id =>
      (signableDocuments ?? []).some(
        doc =>
          doc.contractId === id &&
          (doc.holds ?? []).some(
            h => !h.liftedAt && h.system === 'LendOS' && h.origin.level === 'Trade',
          ),
      ),
    )

  // Representative hold for the Lift modal's detail panel — only when EXACTLY
  // ONE selected sub-trade is locked. With multiple locked sub-trades their
  // holds can differ (reason/placedBy/notes), so showing one would be
  // misleading; we pass null and the modal falls back to a generic message.
  // A sub-trade's `holds` can include cascaded Facility- and SigningPortal-origin
  // holds alongside the LendOS Trade-level hold — liftHoldOnSubTrades always
  // lifts the trade-level document hold, so activeHold must be that specific
  // hold (mirroring the alreadyLocked predicate above), not just any unlifted one.
  const activeHold: Hold | null =
    lockedSelectedSubTradeIds.length === 1
      ? (tradeHolds.find(
        h => !h.liftedAt && h.system === 'LendOS' && h.origin.level === 'Trade',
      ) ?? null)
      : null

  // Lock bulk actions live on the Documents tab for open trades. They are always
  // shown there but disabled until at least one sub-trade is selected (LOS-4955).
  const showLockActions = isDocumentsTab && isOpen

  const placedByName = (() => {
    const userMeta = (user as any)?.['https://rightpedal.com/user_metadata']
    return userMeta?.firstName && userMeta?.lastName
      ? `${userMeta.firstName} ${userMeta.lastName}`
      : user?.email || ''
  })()

  const handlePlaceHoldSubmit = async (payload: HoldPayload): Promise<boolean> => {
    const result: any = await dispatch(
      placeHoldOnSubTrades(
        { subTradeIds: selectedSubTradeIds, ...payload },
        placedByName,
        () => refreshDocuments(),
      ),
    )
    if (!result.success) {
      ErrorNotification({ title: 'Place Lock Failed', message: result.payload })
      return false
    }
    SuccessNotification({
      title: 'Lock Placed',
      message:
        selectedSubTradeIds.length > 1
          ? 'Selected sub-trades have been locked.'
          : 'Sub-trade has been locked.',
    })
    return true
  }

  const handleLiftHoldSubmit = async (liftReason?: string): Promise<boolean> => {
    const result: any = await dispatch(
      liftHoldOnSubTrades(
        {
          subTradeIds: selectedSubTradeIds,
          ...(liftReason ? { liftReason } : {}),
        },
        () => refreshDocuments(),
      ),
    )
    if (!result.success) {
      ErrorNotification({ title: 'Lift Lock Failed', message: result.payload })
      return false
    }
    SuccessNotification({
      title: 'Lock Lifted',
      message:
        selectedSubTradeIds.length > 1
          ? 'Locks on selected sub-trades have been lifted.'
          : 'Sub-trade lock has been lifted.',
    })
    return true
  }

  const confirmDelete = () => {
    handleDeleteDraftTrade(tradeId ?? '')
  }

  // Packet actions can run for a while (a multi-select settled prepare renders
  // one FM PDF per sub-trade client-side; the open packet fetches and zips
  // every document) — show a blocking overlay so the user knows work is going
  // on and can't fire the action twice.
  const busyLabel = downloadingPacket
    ? `Downloading ${selectedSubTradeIds.length} packet(s)…`
    : downloadingEml
      ? `Preparing Settlement Packet for ${selectedSubTradeIds.length} allocation(s)…`
      : preparingOpenPacket
        ? `Preparing Open Trade Packet for ${selectedSubTradeIds.length} allocation(s)…`
        : markingSent
          ? 'Marking deliveries sent…'
          : null

  return (
    <>
      {busyLabel && (
        <Overlay fixed color="#FFFFFF" opacity={0.65} zIndex={1000} data-test-id="packet-action-loader">
          <Stack align="center" justify="center" spacing={12} sx={{ height: '100%' }}>
            {/* CustomLoader (not CustomLoaderPage): the page variant draws the
                light-blue bordered placeholder box, wrong inside an overlay. */}
            <CustomLoader />
            <Text
              role="status"
              aria-live="polite"
              sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 14, fontWeight: 600, color: '#32439A' }}
            >
              {busyLabel}
            </Text>
          </Stack>
        </Overlay>
      )}
      <Menu shadow="md" width={350} position="bottom-start" withinPortal>
        <Menu.Target>
          <div style={{ width: 'fit-content', marginBottom: 12 }}>
            <PrimaryDropDown>Actions</PrimaryDropDown>
          </div>
        </Menu.Target>
        <Menu.Dropdown>
          {isDocumentsTab && (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="download-outline" />}
              disabled={!hasSelection || downloadingPacket}
              onClick={handleDownloadPacket}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Packet
            </Menu.Item>
          )}
          {isDocumentsTab && (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="mail-outline" />}
              disabled={!hasSelection || downloadingEml}
              onClick={handleDownloadEml}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Prepare Settlement Packet
            </Menu.Item>
          )}
          {isDocumentsTab && isOpen && (
            // Deliberately NOT disabled when the AA is unexecuted: the action
            // stays clickable and the backend's refusal is surfaced as an error
            // notification naming the sub-trades to chase (LOS-6934, product
            // decision). One rule, stated once, on the server.
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="mail-unread-outline" />}
              disabled={!hasSelection || preparingOpenPacket}
              onClick={handlePrepareOpenTradePacket}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Prepare Open Trade Packet
            </Menu.Item>
          )}
          {isDocumentsTab && (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="checkmark-done-outline" />}
              disabled={!selectedAllPrepared || markingSent}
              onClick={handleMarkSent}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Mark Sent
            </Menu.Item>
          )}
          {showLockActions && (
            <Tooltip
              label="This trade is already locked"
              disabled={!isTradeAlreadyLocked}
              withinPortal
            >
              <div>
                <Menu.Item
                  icon={<IonIcon className="action-ion-icon" name="lock-closed" />}
                  disabled={!hasSelection || isTradeAlreadyLocked}
                  onClick={() => setShowPlaceLockModal(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Place Lock
                </Menu.Item>
              </div>
            </Tooltip>
          )}
          {showLockActions && (
            <Tooltip
              label="All selected trades must be locked to lift"
              disabled={!hasSelection || allSelectedTradeLocked}
              withinPortal
            >
              <div>
                <Menu.Item
                  icon={<IonIcon className="action-ion-icon" name="lock-open" />}
                  disabled={!hasSelection || !allSelectedTradeLocked}
                  onClick={() => setShowLiftLockModal(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Lift Lock
                </Menu.Item>
              </div>
            </Tooltip>
          )}
          {isDraft && (
            <>
              <Menu.Divider />
              <Menu.Label>Danger zone</Menu.Label>
              <Menu.Item
                color="red"
                icon={
                  <IonIcon className="action-ion-icon" name="trash-outline" />
                }
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Draft
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {showDeleteModal && (
        <DeleteConfirmation
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          message="Are you sure you want to delete this draft?"
        />
      )}
      {showLockActions && (
        <>
          <PlaceHoldModal
            opened={showPlaceLockModal}
            close={() => setShowPlaceLockModal(false)}
            entityNoun="trade"
            onSubmit={handlePlaceHoldSubmit}
          />
          <LiftHoldModal
            opened={showLiftLockModal}
            close={() => setShowLiftLockModal(false)}
            entityNoun="trade"
            hold={activeHold}
            onSubmit={handleLiftHoldSubmit}
          />
        </>
      )}
    </>
  )
}
